import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authService } from "../services/svc_auth";
import { queryKeys } from "../lib/queryKeys";

/**
 * Custom hook for checking authentication status
 * Provides automatic caching, background refresh, and deduplication
 */
export const useTSQAuthStatus = () => {
  return useQuery({
    queryKey: queryKeys.auth.status(),
    queryFn: authService.checkAuthStatus,
    staleTime: 2 * 60 * 1000, // 2 minutes - auth status is relatively stable
    gcTime: 5 * 60 * 1000, // 5 minutes - keep in memory longer
    retry: 1, // Retry once on failure
    refetchOnWindowFocus: true, // Check auth when user returns to tab
    refetchInterval: 5 * 60 * 1000, // Background check every 5 minutes
    refetchOnReconnect: true, // Check auth when network reconnects
  });
};

/**
 * Custom hook for login mutation
 */
export const useTSMLogin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      authService.login(email, password),
    onSuccess: async () => {
      // After successful login, refetch auth status to get user data
      await queryClient.invalidateQueries({
        queryKey: queryKeys.auth.status(),
      });
    },
    onError: (error) => {
      console.error("Login failed:", error);
    },
  });
};

/**
 * Custom hook for logout mutation
 */
export const useTSMLogout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: authService.logout,
    onSuccess: () => {
      // Clear auth cache and set to unauthenticated state
      queryClient.setQueryData(queryKeys.auth.status(), {
        authenticated: false,
      });
      // Also clear all other cached data since user is logged out
      queryClient.clear();
    },
    onError: (error) => {
      console.error("Logout error:", error);
      // Even if logout fails on server, clear local cache
      queryClient.setQueryData(queryKeys.auth.status(), {
        authenticated: false,
      });
      queryClient.clear();
    },
  });
};

/**
 * Custom hook for token refresh mutation
 */
export const useTSMRefreshToken = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: authService.refreshToken,
    onSuccess: () => {
      // After successful refresh, invalidate auth status to get fresh data
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.status() });
    },
    onError: (error) => {
      console.error("Token refresh failed:", error);
      // If refresh fails, user is likely logged out
      queryClient.setQueryData(queryKeys.auth.status(), {
        authenticated: false,
      });
    },
  });
};

export const useTSMForgotPassword = () => {

  return useMutation({
    mutationFn: (email: string) => authService.forgotPassword(email),
    onSuccess: () => {
      console.log("FORGOT PASSWORD SUCCESS");
    },
    onError: (error) => {
      console.error("FORGOT PASSWORD FAILED:", error);
    },
  });
};

export const useTSMResetPassword = () => {

  return useMutation({
    mutationFn: ({ token, password }: { token: string; password: string }) =>
      authService.resetPassword(token, password),
    onSuccess: () => {
      console.log("FORGOT PASSWORD SUCCESS");
    },
    onError: (error) => {
      console.error("FORGOT PASSWORD FAILED:", error);
    },
  });
};

/**
 * Convenience hook that provides the same interface as the original useAuth
 * This makes migration easier by maintaining the same API
 */
export const useAuthQuery = () => {
  const { data: authStatus, isLoading, error } = useTSQAuthStatus();
  const loginMutation = useTSMLogin();
  const logoutMutation = useTSMLogout();
  const queryClient = useQueryClient();

  const login = async (email: string, password: string) => {
    await loginMutation.mutateAsync({ email, password });
  };

  const logout = async () => {
    await logoutMutation.mutateAsync();
  };

  const checkAuth = async () => {
    // Force refetch auth status
    await queryClient.invalidateQueries({ queryKey: queryKeys.auth.status() });
  };

  return {
    user: authStatus?.user || null,
    loading: isLoading || loginMutation.isPending || logoutMutation.isPending,
    isAuthenticated: authStatus?.authenticated || false,
    login,
    logout,
    checkAuth,
    error,
    // Additional useful states
    isLoginPending: loginMutation.isPending,
    isLogoutPending: logoutMutation.isPending,
    loginError: loginMutation.error,
    logoutError: logoutMutation.error,
  };
};
